import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { logger } from '../services/logger';

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  login: () => void;
  logout: () => void;
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // API client for backend services - use relative URL for Traefik routing
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  useEffect(() => {
    // Check if user is authenticated via Keycloak headers
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      // Get user info from Keycloak token in localStorage
      const token = localStorage.getItem('token');
      if (token) {
        // Decode JWT token to get user info (basic implementation)
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const payload = JSON.parse(jsonPayload);

        setUser({
          id: payload.sub || '',
          email: payload.email || '',
          name: payload.name || payload.preferred_username || '',
          roles: payload.realm_access?.roles || [],
          permissions: []
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      logger.error('auth.context', 'Error decoding token', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // Redirect to Keycloak login - this will be handled by OAuth2 Proxy
    window.location.href = '/oauth2/start';
  };

  const logout = () => {
    // Redirect to Keycloak logout
    window.location.href = '/oauth2/sign_out';
  };

  const hasPermission = (permission: string): boolean => {
    return user?.permissions?.includes(permission) || false;
  };

  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || false;
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    loading,
    hasPermission,
    hasRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
